import {
  assertEvent,
  assign,
  fromPromise,
  setup,
  type SnapshotFrom,
} from 'xstate';

import {sendLink} from '../../services/schedularService';

import type {
  IIdentificationMachineContext,
  IIdentificationMachineEvents,
  IIdentificationMachineInput,
  ISendLinkInput,
} from './identificationMachine.types';
import type {AxiosError} from 'axios';
import type {IIdentificationError} from '../../types/dto/IIdentificationError';


export const identificationMachine = setup({
  types: {
    context: {} as IIdentificationMachineContext,
    events: {} as IIdentificationMachineEvents,
    input: {} as IIdentificationMachineInput,
  },
  actors: {
    sendLink: fromPromise(async ({input}: {input: ISendLinkInput}) =>
      sendLink(input.eventId, {
        participant_type: input.participantType,
        reason: input.reason,
        link_type: input.linkType,
      }),
    ),
  },
  guards: {
    isIdentificationAlreadyRequested: ({context}) =>
      context.error?.response?.data.errors?.[0] === 'EventLinkAlreadyRequested',
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QEsJgHYBdkDNkGMBDbAe3QDpl1lMBiHEgJwFtzYBXAI2ZoG0AGALqJQABxKwayMiJAAPRAEYAzADZyAVkUbVigOz9FAJkUBOZRYA0IAJ5L+y8gBYAHEb3nTul6pcOAvv7WqBjYeESkFBzcNNjoULQQZGCU6ABuJADWKSFYuATE0lFcPJhxUAhUGRFFAoJ1suKSkbIKCMam5HqK-KZOvcr8TsqmGtZ2CMoe5Kbmqg5uRqoaLopOgcFoeeGFZGwlsVQJYIyMTOSiADbEDCyUW2EFkfsxZUeV6SQ1ZHUNSCBNKQyf5tNSOYxGVZTbpePTjRBGIydZYOfjzUxGJweFwbEC5R7fCgnM6MWh-MQSIHoVqILTqFYaPS+NyGLFjWyIEZGcj8DT8fhGKYmZamRS4-H5QnkYlMMmKYT-QEtEGIFzKblqnRORHaPROfXwyY+cguDRYxQQxQisVBPEPSW7CiMMCYRg2ABiTGYAGUDnRbqxoqVyQDKcrQKDVJ0jBpRqY0bp9IzDT1HK53J5vL4Arj0CQ0PB-hKduHQ80ijSEL5yGYNMptG5upD+HCOQgALQxpxdQwx5Rm2Om1Ti+0loqpGiNMMVlXtVR6E3qhzKJyiqO6Q3ttU94x1gemIc2zahB3PIOHeJT8vAiNKK010b1lbuYx+VsTIxDcgopb8wzqnQRxPMc9g4fB8DgQsKWvalZ20bkdExA9PxWFdBUNVx+B5WYkx8KN6yWIDtieccZUYK8qUrXQFxcZCXH6PRaLUdkPy0Gsllo3k9G6NxXCIglHXIABBS5nUICAbAAJTAABHdg4EwSAKNLNodBcb9ekFfoplcNQML8bDRRbJxVFMvwjztYCSL2Z1XQ9L1fVeZSZ1vBBTSw18NFjOs1AFJwUwcZxGz6JNIU1QJAiAA */
  context: ({input}) => ({
    eventId: input.eventId,
    linkType: input.linkType,
  }),
  id: 'identification',
  initial: 'init',
  description: `
    Handles the identification form flow during cancel/reschedule
    when the attendee isn't known upfront.
    Attendee info is derived from the URL.
  `,
  states: {
    init: {
      on: {
        'form.submit': {
          target: 'submitting',
        },
      },
    },

    submitting: {
      invoke: {
        input: ({context, event}) => {
          assertEvent(event, 'form.submit');
          return {
            eventId: context.eventId,
            participantType: event.participantType,
            reason: event.reason,
            linkType: context.linkType,
          };
        },

        onDone: {
          target: 'success',
        },

        src: 'sendLink',
        onError: {
          target: 'error',
          actions: assign({
            error: ({event}) => event.error as AxiosError<IIdentificationError>,
          }),
        },
      },
    },

    success: {},

    error: {
      always: [
        {
          target: 'AlreadyRequested',
          guard: {
            type: 'isIdentificationAlreadyRequested',
          },
        },
        {
          target: 'retryFormSubmit',
          reenter: true,
        },
      ],
    },

    AlreadyRequested: {},

    retryFormSubmit: {
      on: {
        'form.submit': {
          target: 'submitting',
          reenter: true,
        },
      },
    },
  },
});

export type IIdentificationMachineSnapshot = SnapshotFrom<
  typeof identificationMachine
>;
